export interface ActivityRow {
    campaign: {
        id?: number | string;
        code?: string;
        title?: string;
        validTimeStart?: string;
        validTimeFinish?: string;
        meetingAddress?: string;
        pcImgMainId?: number | string;
        mobileImgMainId?: number | string;
        offlineAccountId?: number | string;
        paymentQrCodeFileId?: number | string;
        handlerName?: string;
        handlerId?: number | string;
        handlerMobile?: string[];
        state?: number;
        updatedTime?: string;
        createdTime?: string;
        contentConfigs?: number[];
        meetingParticipationConfigs?: number[];
        needAgreeProtocol?: number;
        protocolTitle?: string;
        protocolContent?: string;
        contactInfo?: string;
        scheduleFileId?: number | string;
        meetingSupportFileIds?: Array<number | string>;
        conferenceHotelFileIds?: Array<number | string>;
        pastGlories?: Array<number | string>;
    };
    quantity?: number;
    pcImgMainFile?: ActivityFile;
    mobileImgMainFile?: ActivityFile;
    offlineAccount?: {
        name?: string;
        account?: string;
        openingBank?: string;
    };
    paymentQrCodeFile?: ActivityFile;
    scheduleFile?: ActivityFile;
    meetingSupportFiles?: ActivityFile[];
    conferenceHotelFiles?: ActivityFile[];
    pastGloriesFiles?: ActivityFile[];
    configs?: ActivityRegistrationConfig[];
    demandConfigs?: ActivityDemandConfig[];
}

/** 活动详情媒体文件的最小展示字段，兼容 Vue 文件实体的 path 与历史接口的 url。 */
export interface ActivityFile {
    id?: number | string;
    name?: string;
    path?: string;
    url?: string;
}

/** Vue 活动报名配置卡片的数据结构。 */
export interface ActivityRegistrationConfig {
    type?: number;
    registrationFee?: number;
    maxParticipants?: number;
    fieldConfig?: Array<{ fieldName?: string; hasRequired?: number | boolean }>;
    rightsIntroduction?: string;
    contractTemplateFileId?: number | string;
    isGiveMember?: number;
    giveMemberType?: number;
    giveDuration?: number;
    giveDurationUnit?: number;
    giveCount?: number;
}

export interface ActivityPageResult {
    list?: ActivityRow[];
    total?: number;
}

/** 活动详情关联商品的 Vue 分页返回结构。 */
export interface ActivityProductRow {
    productId?: number | string;
    productCode?: string;
    type?: number;
    addTime?: string;
    mainImgUrl?: string;
    product?: {
        title?: string;
        productTypeName?: string;
        supplierEnterprise?: { name?: string };
        state?: number;
    };
    productCampaign?: { title?: string };
}

export interface ActivityProductPageResult {
    list?: ActivityProductRow[];
    total?: number;
}

/** 活动商品详情接口返回的 Vue 商品活动字段。 */
export interface ActivityProductDetail {
    supplierEnterprise?: { name?: string; enterpriseName?: string };
    /** Vue 详情接口的兼容扁平供应商字段。 */
    supplierName?: string;
    name?: string;
    pricingMethod?: number | string;
    marketPrice?: number;
    estimatedUnitPrice?: number;
    lowerThanMarketPrice?: number;
    stock?: number;
    hasOverbooking?: number | string;
    minOrderTon?: number;
    minAdjustmentTon?: number;
    orderRule?: number | string;
    intentionDeposit?: number;
    tonStep?: number;
    amountStep?: number;
    pictureFileIds?: Array<number | string>;
    videoFileId?: number | string;
    detail?: string;
}

/** 活动商品详情接口的最小响应结构。 */
export interface ActivityProductDetailResult {
    productCampaign?: ActivityProductDetail;
}

export interface ActivityProductQuery {
    page: number;
    size: number;
    keyword?: string;
    productTypeName?: string;
    supplierName?: string;
    type?: number;
    listingState?: number;
    startTime?: string;
    endTime?: string;
}

export interface ActivityQuery {
    page: number;
    size: number;
    code?: string;
    title?: string;
    state?: number | string;
    startTime?: string;
    endTime?: string;
    id?: number | string;
    supplierKeyword?: string;
    activityProduct?: string;
    keyword?: string;
    handlerName?: string;
    payStartTime?: string;
    payEndTime?: string;
    sortKey?: string;
    sortOrder?: string;
    /** Vue 导出接口在选中订单时接收的订单 ID 集合。 */
    ids?: Array<number | string>;
}

/** 活动订单导出参数：未选中时沿用筛选分页，选中时可仅提交订单 ID。 */
export type ActivityOrderExportQuery = Partial<ActivityQuery>;

/** 商品活动新增和编辑页复用 Vue 表单提交的核心字段。 */
export interface ActivityFormValues {
    title: string;
    validTimeStart?: string;
    validTimeFinish?: string;
    meetingAddress?: string;
    pcImgMainId?: number | string;
    mobileImgMainId?: number | string;
    offlineAccountId?: number | string;
    paymentQrCodeFileId?: number | string;
    meetingParticipationConfigs?: number[];
    campaignMeetingTypeConfigForms?: ActivityRegistrationConfig[];
    campaignDemandConfigForms?: ActivityDemandConfig[];
    needAgreeProtocol?: number;
    protocolTitle?: string;
    protocolContent?: string;
    contactInfo?: string;
    scheduleFileId?: number | string;
    meetingSupportFileIds?: Array<number | string> | string;
    conferenceHotelFileIds?: Array<number | string> | string;
    pastGlories?: Array<number | string> | string;
    relatedProductItems?: ActivityRelatedProductItem[] | string;
    contentConfigs?: number[];
    handlerId?: number | string;
    handlerMobile?: string | string[];
}

/** Vue 活动需求对接配置的最小表单结构。 */
export interface ActivityDemandConfig {
    label?: string;
    fieldName?: string;
    hasRequired?: number | boolean;
}

/** Vue 活动表单关联商品的最小提交结构。 */
export interface ActivityRelatedProductItem {
    id?: number | string;
    /** Vue 编辑接口对寄售/团购/捡漏商品返回的关联商品 ID。 */
    productId?: number | string;
    type?: number;
    product?: Record<string, unknown>;
    /** Vue 活动商品类型返回的嵌套活动商品对象。 */
    productCampaign?: Record<string, unknown>;
}

export interface ActivityOrderRow {
    id?: number | string;
    supplierName?: string;
    customerId?: number | string;
    customerEnterprise?: { name?: string; realName?: string; mobile?: string };
    productInfo?: {
        title?: string;
        supplierEnterprise?: { name?: string };
        pricingMethod?: number;
        unitPrice?: number;
        campaignUnitPrice?: number;
        lowerThanMarketPrice?: number;
        stock?: number;
        minOrderTon?: number;
        campaignRule?: string;
        videoId?: number;
        imgIds?: number[];
    };
    payAmount?: number;
    handlerName?: string;
    handlerId?: number | string;
    payTime?: string;
    remark?: string;
}

export interface ActivityOrderPageResult {
    list?: ActivityOrderRow[];
    total?: number;
}

/** 活动订单指派专员沿用 Vue ServiceSpecialVo 的嵌套用户结构。 */
export interface ActivityOrderSpecial {
    user: { id: number | string; name?: string; employeeId?: string };
    orderCount?: number;
}

/** 商品活动指派弹窗中的专员选项。 */
export interface ActivitySpecial {
    /** 真实 ServiceSpecialVo 将专员资料放在 user 中；扁平字段仅兼容历史调用方。 */
    user?: { id?: number | string; personId?: number | string; name?: string; employeeId?: string };
    id?: number | string;
    name?: string;
    employeeId?: string;
    productCount?: number;
}

/** 将真实嵌套 ServiceSpecialVo 与历史扁平数据统一为只读展示结构。 */
export function getActivitySpecialProfile(special: ActivitySpecial) {
    return {
        id: special.id ?? special.user?.personId ?? special.user?.id,
        name: special.user?.name ?? special.name,
        employeeId: special.user?.employeeId ?? special.employeeId
    };
}
